import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listUsers, createUser, deleteUser, type UserRow, type Page } from "../../services/devioz";

type CreateUserForm = {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  rolesCsv: string;
};

export default function Users() {
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState<CreateUserForm>({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    rolesCsv: "",
  });

  useEffect(() => {
    document.title = "Usuarios • Devioz";
  }, []);

  async function refresh(goToPage = page) {
    setLoading(true);
    setErr(null);
    try {
      const res: Page<UserRow> = await listUsers({ q, page: goToPage, pageSize });
      setRows(res.data ?? []);

      const pg = res.pagination ?? { page: goToPage, pageSize, total: res.data?.length ?? 0, pages: 1 };
      setPages(pg.pages ?? 1);
      setTotal(pg.total ?? 0);
      setPage(pg.page ?? goToPage);
    } catch (e: any) {
      setErr(e?.message || "No se pudo cargar el listado");
      setRows([]);
      setPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSearch(e: React.FormEvent) {
    e.preventDefault();
    await refresh(1);
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setMsg(null);

    const first_name = form.first_name.trim();
    const last_name = form.last_name.trim();
    const email = form.email.trim();
    const password = form.password;

    if (!first_name || !last_name || !email || !password) {
      setErr("Nombre, apellido, email y contraseña son obligatorios");
      return;
    }

    const roles = form.rolesCsv
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      setSaving(true);
      await createUser({ first_name, last_name, email, password, roles });
      setMsg(`Usuario "${first_name} ${last_name}" creado`);
      setForm({ first_name: "", last_name: "", email: "", password: "", rolesCsv: "" });
      await refresh(1);
    } catch (e: any) {
      setErr(e?.message || "Error creando usuario");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(u: UserRow) {
    const fullName =
      (u.full_name && u.full_name.trim().length > 0
        ? u.full_name
        : `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()) || u.email;

    if (!window.confirm(`¿Eliminar el usuario "${fullName}"?`)) return;

    setErr(null);
    setMsg(null);

    try {
      await deleteUser(u.id);
      setMsg(`Usuario "${fullName}" eliminado correctamente.`);
      await refresh(page);
    } catch (e: any) {
      setErr(e?.message || "Error eliminando usuario");
    }
  }

  function onPrev() {
    if (page > 1) void refresh(page - 1);
  }
  function onNext() {
    if (page < pages) void refresh(page + 1);
  }

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <Link to="/devioz">Home</Link>
        </li>
        <li className="breadcrumb-item">Devioz</li>
        <li className="breadcrumb-item active">Usuarios</li>
      </ol>

      <h1 className="page-header mb-3">Usuarios (Devioz)</h1>

      {(err || msg) && (
        <div className={`alert ${err ? "alert-danger" : "alert-success"}`}>
          {err || msg}
        </div>
      )}

      <div className="card mb-3">
        <div className="card-body">
          <form className="row gy-2 gx-3 align-items-end" onSubmit={onSearch}>
            <div className="col-md-4">
              <label className="form-label">Buscar</label>
              <input
                className="form-control"
                placeholder="Nombre o email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary w-100" disabled={loading}>
                Buscar
              </button>
            </div>
            <div className="col-md-6 text-end text-muted">
              {total > 0 && <small>{total.toLocaleString()} resultado(s)</small>}
            </div>
          </form>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Nuevo usuario</h5>
          <form className="row gy-2 gx-3" onSubmit={onCreate} noValidate>
            <div className="col-md-3">
              <label className="form-label">Nombres *</label>
              <input
                className="form-control"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                disabled={saving}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Apellidos *</label>
              <input
                className="form-control"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                disabled={saving}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Email *</label>
              <input
                type="email"
                className="form-control"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={saving}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Contraseña *</label>
              <input
                type="password"
                className="form-control"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                disabled={saving}
              />
            </div>
            <div className="col-md-9">
              <label className="form-label">Roles (códigos separados por coma)</label>
              <input
                className="form-control"
                placeholder="DEVIOZ_GERENTE,DEVIOZ_ADMIN"
                value={form.rolesCsv}
                onChange={(e) => setForm({ ...form, rolesCsv: e.target.value })}
                disabled={saving}
              />
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <button className="btn btn-primary w-100" disabled={saving}>
                {saving ? "Guardando…" : "Crear usuario"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-3">Listado</h5>

          {loading ? (
            "Cargando..."
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped align-middle">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Empresa</th>
                      <th>Roles</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((u) => {
                      const full =
                        u.full_name?.trim()
                          ? u.full_name
                          : `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
                      return (
                        <tr key={u.id}>
                          <td>{u.id}</td>
                          <td>{full || "-"}</td>
                          <td>{u.email}</td>
                          <td>{u.company_name ?? u.company_id ?? "-"}</td>
                          <td>{Array.isArray(u.roles) ? u.roles.join(", ") : "-"}</td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => onDelete(u)}
                              title="Eliminar usuario"
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {!rows.length && (
                      <tr>
                        <td colSpan={6} className="text-center">
                          Sin usuarios
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-2">
                <button className="btn btn-outline-secondary" onClick={onPrev} disabled={page <= 1}>
                  « Anterior
                </button>
                <div className="text-muted">
                  Página {page} de {pages}
                </div>
                <button className="btn btn-outline-secondary" onClick={onNext} disabled={page >= pages}>
                  Siguiente »
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
